import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const inquiryLabels = {
  general: "General",
  institutional: "Institutional Inquiry",
  event: "Event Hosting",
  tourism: "Educational Tour",
  corporate: "Corporate Travel",
  partnership: "Partnership",
};

export default function LeadForm({
  inquiryType = "general",
  variant = "light", // "light" | "dark"
  title = "Request a Consultation",
  subtitle = "Share your context. Our institutional team will respond within one business day.",
  testIdPrefix = "lead-form",
  showOrganization = true,
  showCountry = true,
  showRole = true,
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    role: "",
    country: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...form, inquiry_type: inquiryType };
      await api.post("/leads", payload);
      setDone(true);
      toast.success("Thank you. Our team will be in touch shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        organization: "",
        role: "",
        country: "",
        message: "",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isDark = variant === "dark";
  const inputBase = isDark
    ? "w-full bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:border-gold focus:outline-none focus:ring-0"
    : "w-full bg-white border border-slate-200 text-navy placeholder:text-slate-400 focus:border-navy focus:outline-none focus:ring-0";

  const labelBase = isDark ? "text-white/70" : "text-slate-600";

  return (
    <div
      data-testid={`${testIdPrefix}-container`}
      className={`relative ${
        isDark
          ? "bg-navy text-white border border-white/10"
          : "bg-white text-navy border border-slate-200"
      } p-7 sm:p-10`}
    >
      <div className="mb-6">
        <div
          className={`text-[11px] uppercase tracking-[0.22em] ${
            isDark ? "text-gold" : "text-gold-500"
          }`}
        >
          {inquiryLabels[inquiryType] || "Inquiry"}
        </div>
        <h3
          className={`mt-2 font-display text-2xl sm:text-3xl font-light tracking-tight ${
            isDark ? "text-white" : "text-navy"
          }`}
        >
          {title}
        </h3>
        <p className={`mt-2 text-sm ${isDark ? "text-white/70" : "text-slate-600"}`}>
          {subtitle}
        </p>
      </div>

      {done ? (
        <div
          data-testid={`${testIdPrefix}-success`}
          className={`p-6 border ${
            isDark ? "border-gold/40 bg-gold/5" : "border-navy/10 bg-navy/5"
          }`}
        >
          <div className="text-sm uppercase tracking-widest text-gold mb-2">
            Received
          </div>
          <div className={isDark ? "text-white" : "text-navy"}>
            Thank you for reaching out. A member of our institutional team will
            contact you shortly.
          </div>
          <button
            data-testid={`${testIdPrefix}-reset`}
            onClick={() => setDone(false)}
            className={`mt-5 text-xs uppercase tracking-[0.18em] ${
              isDark ? "text-gold hover:text-gold-300" : "text-navy hover:opacity-80"
            }`}
          >
            Submit another inquiry →
          </button>
        </div>
      ) : (
        <form
          data-testid={`${testIdPrefix}-form`}
          onSubmit={submit}
          className="space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={`block text-xs uppercase tracking-wider mb-2 ${labelBase}`}>
                Full Name *
              </label>
              <input
                required
                name="name"
                value={form.name}
                onChange={onChange}
                data-testid={`${testIdPrefix}-name`}
                className={`${inputBase} px-4 py-3`}
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className={`block text-xs uppercase tracking-wider mb-2 ${labelBase}`}>
                Email *
              </label>
              <input
                required
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                data-testid={`${testIdPrefix}-email`}
                className={`${inputBase} px-4 py-3`}
                placeholder="you@institution.org"
              />
            </div>
            <div>
              <label className={`block text-xs uppercase tracking-wider mb-2 ${labelBase}`}>
                Phone / WhatsApp
              </label>
              <input
                name="phone"
                value={form.phone}
                onChange={onChange}
                data-testid={`${testIdPrefix}-phone`}
                className={`${inputBase} px-4 py-3`}
                placeholder="+971 ..."
              />
            </div>
            {showCountry ? (
              <div>
                <label className={`block text-xs uppercase tracking-wider mb-2 ${labelBase}`}>
                  Country
                </label>
                <input
                  name="country"
                  value={form.country}
                  onChange={onChange}
                  data-testid={`${testIdPrefix}-country`}
                  className={`${inputBase} px-4 py-3`}
                  placeholder="United Arab Emirates"
                />
              </div>
            ) : null}
            {showOrganization ? (
              <div className={showRole ? "" : "sm:col-span-2"}>
                <label className={`block text-xs uppercase tracking-wider mb-2 ${labelBase}`}>
                  Institution / Organisation
                </label>
                <input
                  name="organization"
                  value={form.organization}
                  onChange={onChange}
                  data-testid={`${testIdPrefix}-organization`}
                  className={`${inputBase} px-4 py-3`}
                  placeholder="Your institution name"
                />
              </div>
            ) : null}
            {showRole ? (
              <div>
                <label className={`block text-xs uppercase tracking-wider mb-2 ${labelBase}`}>
                  Role
                </label>
                <input
                  name="role"
                  value={form.role}
                  onChange={onChange}
                  data-testid={`${testIdPrefix}-role`}
                  className={`${inputBase} px-4 py-3`}
                  placeholder="Principal, Director, Coordinator…"
                />
              </div>
            ) : null}
          </div>

          <div>
            <label className={`block text-xs uppercase tracking-wider mb-2 ${labelBase}`}>
              How can we help? *
            </label>
            <textarea
              required
              rows={4}
              name="message"
              value={form.message}
              onChange={onChange}
              data-testid={`${testIdPrefix}-message`}
              className={`${inputBase} px-4 py-3 resize-none`}
              placeholder="Briefly describe your context, scale, and timeline."
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid={`${testIdPrefix}-submit`}
            className={`group inline-flex items-center gap-3 px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition ${
              isDark
                ? "bg-gold text-navy hover:bg-gold-300"
                : "bg-navy text-white hover:bg-navy-700"
            } disabled:opacity-60`}
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5"
              />
            )}
            Submit Inquiry
          </button>
          <p className={`text-[11px] ${isDark ? "text-white/50" : "text-slate-500"}`}>
            By submitting, you agree to be contacted by Destineze regarding your
            inquiry.
          </p>
        </form>
      )}
    </div>
  );
}
